/**
 * CompletionRuleEvaluator - Service for automatically completing tasks based on completion rules
 * 
 * This service evaluates completion rules defined in milestone templates and automatically
 * marks tasks as complete when their conditions are satisfied.
 */

import { supabase } from '../lib/supabase.js';

export class CompletionRuleEvaluator {
  /**
   * Evaluate all tasks for a user and auto-complete/incomplete based on rules
   */
  static async evaluateUserTasks(userId) {
    try {
      console.log(`🔄 [CompletionRuleEvaluator] Evaluating completion rules for user ${userId}`);

      // Get all timeline items with completion rules (both completed and incomplete)
      const { data: allTasks, error: tasksError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .not('completion_rule', 'is', null);

      if (tasksError) {
        console.error('Error fetching tasks:', tasksError);
        return { completed: 0, incompleted: 0, errors: [tasksError.message] };
      }

      if (!allTasks?.length) {
        console.log('✅ [DEBUG] No tasks with completion rules found');
        return { completed: 0, incompleted: 0, errors: [] };
      }

      console.log(`📋 [DEBUG] Found ${allTasks.length} tasks with completion rules to evaluate`);
      console.log('🔍 [DEBUG] Tasks with completion rules:', allTasks.map(t => ({
        id: t.id,
        title: t.title,
        completion_rule: t.completion_rule,
        completed: t.completed,
        completion_status: t.completion_status
      })));

      // Log which rules we're looking for
      console.log('📌 [DEBUG] Looking for these completion rules:');
      const uniqueRules = [...new Set(allTasks.map(t => t.completion_rule))];
      uniqueRules.forEach(rule => {
        const count = allTasks.filter(t => t.completion_rule === rule).length;
        console.log(`  - ${rule}: ${count} task(s)`);
      });

      // Get user's college data for rule evaluation
      const userData = await this.getUserData(userId);
      console.log('🔍 [DEBUG] User data for rule evaluation:', {
        userId: userData.userId,
        totalColleges: userData.totalColleges,
        collegesByCategory: userData.collegesByCategory,
        hasAssessment: !!userData.assessment,
        academicProfileKeys: userData.academic ? Object.keys(userData.academic) : [],
        activitiesCount: userData.activities?.length || 0
      });

      const results = { completed: 0, incompleted: 0, errors: [] };

      // Evaluate each task
      for (const task of allTasks) {
        try {
          console.log(`\n🔄 [TASK EVAL] Processing task: "${task.title}"`);
          console.log(`  - Task ID: ${task.id}`);
          console.log(`  - Completion Rule: ${task.completion_rule}`);
          console.log(`  - Current Status: completed=${task.completed}, completion_status=${task.completion_status}, workflow_phase=${task.workflow_phase}, status=${task.status}`);

          // Pass task ID in userData for status updates
          userData.currentTaskId = task.id;

          const shouldComplete = await this.evaluateCompletionRule(task.completion_rule, userData, task);
          console.log(`  - Rule Evaluation Result: ${shouldComplete}`);

          // Check if completion fields are consistent
          const isFullyCompleted = task.completed &&
                                   task.completion_status === 'completed' &&
                                   task.workflow_phase === 'complete' &&
                                   task.status === 'completed';

          if (shouldComplete && !isFullyCompleted) {
            // Task should be complete but fields are inconsistent - mark it complete
            console.log(`  - ACTION: Marking task as complete (shouldComplete=true, fully completed=${isFullyCompleted})`);
            const success = await this.markTaskComplete(task.id, task.title);
            if (success) {
              results.completed++;
              console.log(`✅ Auto-completed: ${task.title}`);
            } else {
              console.error(`❌ Failed to mark task complete: ${task.title}`);
            }
          } else if (!shouldComplete && task.completed) {
            // Task is completed but no longer meets criteria - mark it incomplete
            // Note: We only auto-incomplete tasks to prevent undoing manual completions
            console.log(`  - ACTION: Marking task as incomplete (shouldComplete=false, currently completed=true)`);
            const success = await this.markTaskIncomplete(task.id, task.title);
            if (success) {
              results.incompleted++;
              console.log(`❌ Auto-incompleted: ${task.title}`);
            } else {
              console.error(`❌ Failed to mark task incomplete: ${task.title}`);
            }
          } else {
            console.log(`  - ACTION: No change needed (shouldComplete=${shouldComplete}, fully completed=${isFullyCompleted})`);
          }
        } catch (error) {
          console.error(`Error evaluating task ${task.title}:`, error);
          results.errors.push(`${task.title}: ${error.message}`);
        }
      }

      console.log(`🎉 Rule evaluation complete: ${results.completed} completed, ${results.incompleted} incompleted`);
      return results;

    } catch (error) {
      console.error('Error in evaluateUserTasks:', error);
      return { completed: 0, errors: [error.message] };
    }
  }

  /**
   * Get user data needed for rule evaluation
   */
  static async getUserData(userId) {
    try {
      // Get college data
      const { data: colleges, error: collegesError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('user_id', userId);

      if (collegesError) throw collegesError;

      // Get profile data for profile completion rules
      const [academicResult, activitiesResult, assessmentResult, bragSheetResult] = await Promise.all([
        // Academic profile data (includes completion locks)
        supabase
          .from('student_academic_profiles')
          .select('*, locked_college_categories, custom_college_targets')
          .eq('user_id', userId)
          .single(),
        
        // Student activities data
        supabase
          .from('student_activities')
          .select('*')
          .eq('user_id', userId),
        
        // Assessment completion
        supabase
          .from('assessment_responses')
          .select('id, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1),
        
        // Brag sheet data (stored in profiles or separate table)
        supabase
          .from('student_academic_profiles')
          .select('ai_strategic_summary, ai_executive_summary, ai_strategic_summary_updated_at')
          .eq('user_id', userId)
          .single()
      ]);

      // Group colleges by category for easy counting
      const collegesByCategory = colleges?.reduce((acc, college) => {
        const category = college.category || 'other';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {}) || {};

      // Count Early Decision and Early Action schools
      const earlyDecisionCount = colleges?.filter(c => c.application_round === 'early_decision').length || 0;
      const earlyActionCount = colleges?.filter(c => c.application_round === 'early_action').length || 0;

      return {
        userId,
        colleges: colleges || [],
        collegesByCategory,
        totalColleges: colleges?.length || 0,
        earlyDecisionCount,
        earlyActionCount,
        // Profile data for new rules
        academic: academicResult.data || {},
        activities: activitiesResult.data || [],
        assessment: assessmentResult.data ? assessmentResult.data[0] : null,
        bragSheet: bragSheetResult.data || {},
        // Completion lock data
        lockedCategories: academicResult.data?.locked_college_categories || {},
        customTargets: academicResult.data?.custom_college_targets || {},
        errors: {
          academic: academicResult.error,
          activities: activitiesResult.error,
          assessment: assessmentResult.error,
          bragSheet: bragSheetResult.error
        }
      };
    } catch (error) {
      console.error('Error fetching user data for rules:', error);
      throw error;
    }
  }

  /**
   * Evaluate a specific completion rule
   */
  static async evaluateCompletionRule(rule, userData, task) {
    if (!rule) return false;

    console.log(`🔍 Evaluating rule '${rule}' for task: ${task.title}`);

    switch (rule) {
      case 'select_reach_schools':
        return this.evaluateReachSchoolsRule(userData);

      case 'select_target_schools':
        return this.evaluateTargetSchoolsRule(userData);

      case 'select_safety_schools':
        return this.evaluateSafetySchoolsRule(userData);

      case 'complete_college_list':
        return this.evaluateCompleteCollegeListRule(userData);

      // Profile completion rules
      case 'complete_basic_information':
        return this.evaluateBasicInformationRule(userData);

      case 'add_academic_information':
        return this.evaluateAcademicInformationRule(userData);

      case 'complete_academic_profile':
        return this.evaluateCompleteAcademicProfileRule(userData);

      case 'finalize_activities_list':
        return this.evaluateActivitiesListRule(userData);

      case 'prepare_brag_sheet':
        return this.evaluateBragSheetRule(userData);

      case 'take_strategic_profile_assessment':
      case 'strategic_assessment_complete':
        return this.evaluateAssessmentRule(userData);

      // New school finalization rules
      case 'finalize_reach_schools':
        return this.evaluateFinalizeReachSchoolsRule(userData);

      case 'finalize_target_schools':
        return this.evaluateFinalizeTargetSchoolsRule(userData);

      case 'finalize_safety_schools':
        return this.evaluatefinalizeSafetySchoolsRule(userData);

      // College data verification
      case 'college_data_verified':
      case 'verify_college_data':
        return this.evaluateCollegeDataVerifiedRule(userData, task);

      // Early Decision / Early Action selection
      case 'select_early_decision_school':
      case 'has_early_decision':
        return this.evaluateEarlyDecisionRule(userData);

      case 'select_early_action_school':
      case 'has_early_action':
        return this.evaluateEarlyActionRule(userData);

      // Add more rules as needed
      default:
        console.log(`⚠️ Unknown completion rule: ${rule}`);
        return false;
    }
  }

  /**
   * Rule: Check if student has selected enough reach schools (reach category)
   * This is for the basic selection task
   */
  static evaluateReachSchoolsRule(userData) {
    const reachCount = userData.collegesByCategory.reach || 0;
    const customTarget = userData.customTargets?.reach || 4;
    const minTarget = 4;

    console.log(`🔍 [RULE DEBUG] Reach Schools Rule Evaluation:`);
    console.log(`  - Reach count: ${reachCount}`);
    console.log(`  - Custom target: ${customTarget}`);
    console.log(`  - Min target: ${minTarget}`);

    // Use custom target if set, otherwise use default
    const target = customTarget || minTarget;
    const isComplete = reachCount >= target;

    console.log(`  - Target: ${target}`);
    console.log(`  - Calculation: ${reachCount} ≥ ${target} = ${isComplete}`);
    console.log(`  - RESULT: ${isComplete ? 'TRUE' : 'FALSE'}`);

    console.log(`📊 Reach schools: ${reachCount}/${target} (${isComplete ? 'COMPLETE' : 'incomplete'})`);
    return isComplete;
  }

  /**
   * Rule: Check if student has finalized reach schools (selected AND locked)
   * This is for the finalization task
   */
  static evaluateFinalizeReachSchoolsRule(userData) {
    const reachCount = userData.collegesByCategory.reach || 0;
    const isLocked = userData.lockedCategories?.reach || false;
    const target = 4;

    console.log(`🔍 [RULE DEBUG] Finalize Reach Schools Rule Evaluation:`);
    console.log(`  - Reach count: ${reachCount}`);
    console.log(`  - Is locked: ${isLocked}`);
    console.log(`  - Target: ${target}`);

    // If locked with enough schools, task is complete
    if (isLocked && reachCount >= target) {
      console.log(`  - RESULT: TRUE (locked with ${reachCount} schools)`);
      console.log(`📊 Reach schools: ${reachCount} (LOCKED - COMPLETE)`);
      return true;
    }

    // If slots filled but not locked, task is ready to complete but not done
    if (!isLocked && reachCount >= target) {
      console.log(`  - RESULT: FALSE (${reachCount} schools selected but NOT LOCKED)`);
      console.log(`📊 Reach schools: ${reachCount}/${target} slots filled - NEED TO LOCK`);

      // Store status info in task metadata for UI display
      if (userData.currentTaskId) {
        this.updateTaskStatus(userData.currentTaskId, {
          status_message: `📌 ${reachCount}/4 slots filled - lock to complete`,
          action_required: 'lock_reach_schools',
          ready_to_lock: true
        });
      }
      return false;
    }

    // Still in progress
    console.log(`  - RESULT: FALSE (only ${reachCount}/${target} schools selected)`);
    return false;
  }

  /**
   * Rule: Check if student has selected enough target schools
   * This is for the basic selection task
   */
  static evaluateTargetSchoolsRule(userData) {
    const targetCount = userData.collegesByCategory.target || 0;
    const customTarget = userData.customTargets?.target || 4;
    const minTarget = 4;

    // Use custom target if set, otherwise use default
    const target = customTarget || minTarget;
    const isComplete = targetCount >= target;

    console.log(`📊 Target schools: ${targetCount}/${target} (${isComplete ? 'COMPLETE' : 'incomplete'})`);
    return isComplete;
  }

  /**
   * Rule: Check if student has finalized target schools (selected AND locked)
   * This is for the finalization task
   */
  static evaluateFinalizeTargetSchoolsRule(userData) {
    const targetCount = userData.collegesByCategory.target || 0;
    const isLocked = userData.lockedCategories?.target || false;
    const target = 4;

    console.log(`🔍 [RULE DEBUG] Finalize Target Schools Rule Evaluation:`);
    console.log(`  - Target count: ${targetCount}`);
    console.log(`  - Is locked: ${isLocked}`);
    console.log(`  - Target: ${target}`);

    // If locked with enough schools, task is complete
    if (isLocked && targetCount >= target) {
      console.log(`  - RESULT: TRUE (locked with ${targetCount} schools)`);
      console.log(`📊 Target schools: ${targetCount} (LOCKED - COMPLETE)`);
      return true;
    }

    // If slots filled but not locked, task is ready to complete but not done
    if (!isLocked && targetCount >= target) {
      console.log(`  - RESULT: FALSE (${targetCount} schools selected but NOT LOCKED)`);
      console.log(`📊 Target schools: ${targetCount}/${target} slots filled - NEED TO LOCK`);

      // Store status info in task metadata for UI display
      if (userData.currentTaskId) {
        this.updateTaskStatus(userData.currentTaskId, {
          status_message: `📌 ${targetCount}/4 slots filled - lock to complete`,
          action_required: 'lock_target_schools',
          ready_to_lock: true
        });
      }
      return false;
    }

    // Still in progress
    console.log(`  - RESULT: FALSE (only ${targetCount}/${target} schools selected)`);
    return false;
  }

  /**
   * Rule: Check if student has selected enough safety schools
   * This is for the basic selection task
   */
  static evaluateSafetySchoolsRule(userData) {
    const safetyCount = userData.collegesByCategory.safety || 0;
    const customTarget = userData.customTargets?.safety || 4;
    const minTarget = 4;

    console.log(`🔍 [RULE DEBUG] Safety Schools Rule Evaluation:`);
    console.log(`  - Safety count: ${safetyCount}`);
    console.log(`  - Custom target: ${customTarget}`);
    console.log(`  - Min target: ${minTarget}`);

    // Use custom target if set, otherwise use default
    const target = customTarget || minTarget;
    const isComplete = safetyCount >= target;

    console.log(`  - Target: ${target}`);
    console.log(`  - Calculation: ${safetyCount} ≥ ${target} = ${isComplete}`);
    console.log(`  - RESULT: ${isComplete ? 'TRUE' : 'FALSE'}`);

    console.log(`📊 Safety schools: ${safetyCount}/${target} (${isComplete ? 'COMPLETE' : 'incomplete'})`);
    return isComplete;
  }

  /**
   * Rule: Check if student has finalized safety schools (selected AND locked)
   * This is for the finalization task
   */
  static evaluatefinalizeSafetySchoolsRule(userData) {
    const safetyCount = userData.collegesByCategory.safety || 0;
    const isLocked = userData.lockedCategories?.safety || false;
    const target = 4;

    console.log(`🔍 [RULE DEBUG] Finalize Safety Schools Rule Evaluation:`);
    console.log(`  - Safety count: ${safetyCount}`);
    console.log(`  - Is locked: ${isLocked}`);
    console.log(`  - Target: ${target}`);

    // If locked with enough schools, task is complete
    if (isLocked && safetyCount >= target) {
      console.log(`  - RESULT: TRUE (locked with ${safetyCount} schools)`);
      console.log(`📊 Safety schools: ${safetyCount} (LOCKED - COMPLETE)`);
      return true;
    }

    // If slots filled but not locked, task is ready to complete but not done
    if (!isLocked && safetyCount >= target) {
      console.log(`  - RESULT: FALSE (${safetyCount} schools selected but NOT LOCKED)`);
      console.log(`📊 Safety schools: ${safetyCount}/${target} slots filled - NEED TO LOCK`);

      // Store status info in task metadata for UI display
      if (userData.currentTaskId) {
        this.updateTaskStatus(userData.currentTaskId, {
          status_message: `📌 ${safetyCount}/4 slots filled - lock to complete`,
          action_required: 'lock_safety_schools',
          ready_to_lock: true
        });
      }
      return false;
    }

    // Still in progress
    console.log(`  - RESULT: FALSE (only ${safetyCount}/${target} schools selected)`);
    return false;
  }

  /**
   * Rule: Check if student has a complete college list (balanced across categories)
   */
  static evaluateCompleteCollegeListRule(userData) {
    const reach = userData.collegesByCategory.reach || 0;
    const target = userData.collegesByCategory.target || 0;
    const safety = userData.collegesByCategory.safety || 0;
    
    const isComplete = reach >= 4 && target >= 4 && safety >= 4;
    
    console.log(`📊 Complete list: ${reach} reach, ${target} target, ${safety} safety (${isComplete ? 'COMPLETE' : 'incomplete'})`);
    return isComplete;
  }

  /**
   * Rule: Check if student has completed basic information
   */
  static evaluateBasicInformationRule(userData) {
    const profile = userData.academic;
    const basicFields = [
      'grade_level', 'graduation_year', 'state_of_residency',
      'high_school_name', 'high_school_city', 'high_school_state'
    ];
    
    let completedFields = 0;
    const requiredFields = 4; // At least 4 of 6 basic fields
    
    basicFields.forEach(field => {
      const value = profile[field];
      if (value !== null && value !== undefined && value !== '') {
        completedFields++;
      }
    });
    
    const isComplete = completedFields >= requiredFields;
    console.log(`📊 Basic information: ${completedFields}/${basicFields.length} fields (${isComplete ? 'COMPLETE' : 'incomplete'})`);
    return isComplete;
  }

  /**
   * Rule: Check if student has added academic information
   */
  static evaluateAcademicInformationRule(userData) {
    const profile = userData.academic;
    const academicFields = [
      'gpa_weighted', 'gpa_unweighted', 'gpa_scale',
      'ap_courses_taken', 'honors_courses', 'curriculum_rigor'
    ];
    
    let completedFields = 0;
    const requiredFields = 3; // At least 3 of 6 academic fields
    
    academicFields.forEach(field => {
      const value = profile[field];
      if (value !== null && value !== undefined && 
          (typeof value === 'number' || (typeof value === 'string' && value !== ''))) {
        completedFields++;
      }
    });
    
    const isComplete = completedFields >= requiredFields;
    console.log(`📊 Academic information: ${completedFields}/${academicFields.length} fields (${isComplete ? 'COMPLETE' : 'incomplete'})`);
    return isComplete;
  }

  /**
   * Rule: Check if student has completed both basic and academic profile sections
   */
  static evaluateCompleteAcademicProfileRule(userData) {
    const basicComplete = this.evaluateBasicInformationRule(userData);
    const academicComplete = this.evaluateAcademicInformationRule(userData);
    
    const isComplete = basicComplete && academicComplete;
    console.log(`📊 Complete Academic Profile: Basic(${basicComplete}) + Academic(${academicComplete}) = ${isComplete ? 'COMPLETE' : 'incomplete'}`);
    return isComplete;
  }

  /**
   * Rule: Check if student has finalized their activities list
   */
  static evaluateActivitiesListRule(userData) {
    const activities = userData.activities;
    const minActivities = 2; // At least 2 meaningful activities
    
    // Count activities with substantial information
    const meaningfulActivities = activities.filter(activity => {
      return activity.activity_name && 
             activity.activity_name.trim() !== '' &&
             activity.hours_per_week > 0; // Must have time commitment
    });
    
    const isComplete = meaningfulActivities.length >= minActivities;
    console.log(`📊 Activities list: ${meaningfulActivities.length}/${minActivities} activities (${isComplete ? 'COMPLETE' : 'incomplete'})`);
    return isComplete;
  }

  /**
   * Rule: Check if student has prepared their brag sheet
   */
  static evaluateBragSheetRule(userData) {
    const bragSheet = userData.bragSheet;
    
    // Check if AI summaries are generated (indicator of brag sheet completion)
    const hasStrategicSummary = bragSheet.ai_strategic_summary && 
                               bragSheet.ai_strategic_summary.trim() !== '';
    const hasExecutiveSummary = bragSheet.ai_executive_summary && 
                               bragSheet.ai_executive_summary.trim() !== '';
    const hasRecentUpdate = bragSheet.ai_strategic_summary_updated_at &&
                           new Date(bragSheet.ai_strategic_summary_updated_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // Within 30 days
    
    const isComplete = hasStrategicSummary || hasExecutiveSummary;
    console.log(`📊 Brag sheet: Strategic(${hasStrategicSummary}) Executive(${hasExecutiveSummary}) Recent(${hasRecentUpdate}) = ${isComplete ? 'COMPLETE' : 'incomplete'}`);
    return isComplete;
  }

  /**
   * Rule: Check if student has taken the strategic profile assessment
   */
  static evaluateAssessmentRule(userData) {
    const assessment = userData.assessment;

    if (!assessment) {
      console.log(`📊 Strategic assessment: incomplete (not started)`);
      return false;
    }

    // Check if assessment is recent (within 6 months)
    const assessmentDate = new Date(assessment.created_at);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const isRecent = assessmentDate > sixMonthsAgo;
    const isComplete = assessment !== null && assessment !== undefined && isRecent;

    if (isComplete) {
      console.log(`📊 Strategic assessment: COMPLETE (taken ${assessment.created_at})`);
    } else if (assessment && !isRecent) {
      console.log(`📊 Strategic assessment: needs update (over 6 months old)`);
    } else {
      console.log(`📊 Strategic assessment: incomplete`);
    }

    return isComplete;
  }

  /**
   * Rule: Check if college data has been verified for a specific college
   */
  static async evaluateCollegeDataVerifiedRule(userData, task) {
    const collegeId = task.college_id || task.source_college_id;

    if (!collegeId) {
      console.log(`📊 College data verification: No college ID in task`);
      return false;
    }

    const college = userData.colleges?.find(c => c.id === collegeId);

    if (!college) {
      console.log(`📊 College data verification: College not found`);
      return false;
    }

    // Import and use the CollegeDataCompleteness utility
    try {
      const { CollegeDataCompleteness } = await import('../utils/collegeDataCompleteness');
      const validation = CollegeDataCompleteness.validateCollege(college, {
        deadlines: userData.deadlines,
        globalCollegeData: userData.globalCollegeData
      });

      if (validation.isComplete) {
        console.log(`✅ ${college.name || college.college_name} data verified - all required fields present`);
        return true;
      }

      if (validation.readyForTasks) {
        console.log(`⚠️ ${college.name || college.college_name} data partially complete - ${validation.missing.join(', ')} missing`);

        // Store status for UI display
        if (userData.currentTaskId) {
          this.updateTaskStatus(userData.currentTaskId, {
            status_message: `⚠️ Data partially complete - missing: ${validation.missing.join(', ')}`,
            action_required: 'complete_college_data',
            partial_complete: true
          });
        }
        return false;
      }

      console.log(`❌ ${college.name || college.college_name} missing required data: ${validation.missing.join(', ')}`);
      return false;

    } catch (error) {
      console.error('Error evaluating college data verification:', error);
      return false;
    }
  }

  /**
   * Rule: Check if student has selected at least one Early Decision school
   */
  static evaluateEarlyDecisionRule(userData) {
    const edCount = userData.earlyDecisionCount || 0;
    const isComplete = edCount >= 1;

    console.log(`🔍 [RULE DEBUG] Early Decision Rule Evaluation:`);
    console.log(`  - Early Decision schools: ${edCount}`);
    console.log(`  - RESULT: ${isComplete ? 'TRUE (has ED school)' : 'FALSE (no ED school)'}`);
    console.log(`📊 Early Decision: ${edCount} school${edCount !== 1 ? 's' : ''} (${isComplete ? 'COMPLETE' : 'incomplete'})`);

    return isComplete;
  }

  /**
   * Rule: Check if student has selected at least one Early Action school
   */
  static evaluateEarlyActionRule(userData) {
    const eaCount = userData.earlyActionCount || 0;
    const isComplete = eaCount >= 1;

    console.log(`🔍 [RULE DEBUG] Early Action Rule Evaluation:`);
    console.log(`  - Early Action schools: ${eaCount}`);
    console.log(`  - RESULT: ${isComplete ? 'TRUE (has EA school)' : 'FALSE (no EA school)'}`);
    console.log(`📊 Early Action: ${eaCount} school${eaCount !== 1 ? 's' : ''} (${isComplete ? 'COMPLETE' : 'incomplete'})`);

    return isComplete;
  }

  /**
   * Mark a task as complete in the database
   */
  static async markTaskComplete(taskId, taskTitle) {
    try {
      console.log(`🔍 [DB UPDATE] Marking task complete: ${taskTitle} (ID: ${taskId})`);

      const { data, error } = await supabase
        .from('timeline_items')
        .update({
          completed: true,
          completion_status: 'completed',
          workflow_phase: 'complete',
          status: 'completed',
          completion_date: new Date().toISOString()
        })
        .eq('id', taskId)
        .select(); // Return the updated record for debugging

      if (error) {
        console.error(`❌ [DB UPDATE] Error marking task complete: ${taskTitle}`, error);
        return false;
      }

      console.log(`✅ [DB UPDATE] Successfully marked task complete:`, data);

      // Log the auto-completion
      await this.logCompletion(taskId, 'auto_complete', data[0]);

      return true;
    } catch (error) {
      console.error(`❌ [DB UPDATE] Exception marking task complete: ${taskTitle}`, error);
      return false;
    }
  }

  /**
   * Mark a task as incomplete in the database
   */
  static async markTaskIncomplete(taskId, taskTitle) {
    try {
      const { data, error } = await supabase
        .from('timeline_items')
        .update({
          completed: false,
          completion_status: 'not_started',
          workflow_phase: 'ready',
          status: 'planned',
          completion_date: null
        })
        .eq('id', taskId)
        .select();

      if (error) {
        console.error(`Error marking task incomplete: ${taskTitle}`, error);
        return false;
      }

      // Log the auto-incomplete
      await this.logCompletion(taskId, 'auto_incomplete', data[0]);

      return true;
    } catch (error) {
      console.error(`Error marking task incomplete: ${taskTitle}`, error);
      return false;
    }
  }

  /**
   * Evaluate completion rules for a specific task (used for targeted evaluation)
   */
  static async evaluateSpecificTask(taskId) {
    try {
      const { data: task, error: taskError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('id', taskId)
        .single();

      if (taskError || !task) {
        console.error('Task not found:', taskError);
        return false;
      }

      if (task.completed || !task.completion_rule) {
        return false; // Already complete or no rule to evaluate
      }

      const userData = await this.getUserData(task.user_id);
      userData.currentTaskId = task.id; // For status updates
      const shouldComplete = await this.evaluateCompletionRule(task.completion_rule, userData, task);

      if (shouldComplete) {
        return await this.markTaskComplete(task.id, task.title);
      }

      return false;
    } catch (error) {
      console.error('Error evaluating specific task:', error);
      return false;
    }
  }

  /**
   * Update task status metadata for UI display
   * This stores temporary status information that the UI can use to show helpful messages
   */
  static async updateTaskStatus(taskId, statusInfo) {
    try {
      // Get the current task metadata
      const { data: task, error: fetchError } = await supabase
        .from('timeline_items')
        .select('metadata')
        .eq('id', taskId)
        .single();

      if (fetchError || !task) {
        console.error('Error fetching task for status update:', fetchError);
        return false;
      }

      // Update metadata with status info
      const updatedMetadata = {
        ...(task.metadata || {}),
        completion_status_info: {
          ...statusInfo,
          updated_at: new Date().toISOString()
        }
      };

      // Update the task with new metadata
      const { error: updateError } = await supabase
        .from('timeline_items')
        .update({ metadata: updatedMetadata })
        .eq('id', taskId);

      if (updateError) {
        console.error('Error updating task status:', updateError);
        return false;
      }

      console.log(`📝 Updated task status for ${taskId}:`, statusInfo);
      return true;
    } catch (error) {
      console.error('Error in updateTaskStatus:', error);
      return false;
    }
  }

  /**
   * Log a completion event to the completion_log table
   */
  static async logCompletion(taskId, completionType, taskData) {
    try {
      const logEntry = {
        task_id: taskId,
        user_id: taskData.user_id,
        completion_type: completionType,
        completion_system: 'completion_rule',
        trigger_source: 'completion_rule_evaluator',
        completion_rule: taskData.completion_rule,
        task_title: taskData.title,
        task_category: taskData.category,
        college_id: taskData.college_id,
        college_name: taskData.college_name,
        metadata: {
          completion_status: taskData.completion_status,
          workflow_phase: taskData.workflow_phase,
          status: taskData.status
        }
      };

      const { error } = await supabase
        .from('completion_log')
        .insert(logEntry);

      if (error) {
        console.error('Error logging completion:', error);
        return false;
      }

      console.log(`📊 Logged ${completionType} for task ${taskId}`);
      return true;
    } catch (error) {
      console.error('Error in logCompletion:', error);
      return false;
    }
  }
}

export default CompletionRuleEvaluator;